import { Avatar, Col, Popover, Row, Tag } from "antd";
import { Charts, ProgressBar } from "shared/components";
import { ReactComponent as RankingIcon } from "shared/assets/images/ranking-icon.svg";
import { ReactComponent as SpotifyIcon } from "shared/assets/images/spotify-icon.svg";
import { ReactComponent as YoutubeIcon } from "shared/assets/images/youtube-icon.svg";
import { ReactComponent as InstagramIcon } from "shared/assets/images/instagram-icon.svg";
import { ReactComponent as EngagementIcon } from "shared/assets/images/engagement-icon.svg";
import { ReactComponent as FollowersIcon } from "shared/assets/images/followers-icon.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowUp,
  faStop,
} from "@fortawesome/free-solid-svg-icons";
import careerStages from "shared/constants/careerStages";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getI18nGenre } from "shared/helpers/i18nGenre";
import { getI18nCoutry } from "shared/helpers/i18nCountry";
import { srcToBase64 } from "shared/helpers/srcToBase64";
import { useEffect, useState } from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import moment from "moment";

interface CareerProfileProps {
  publicData: any;
}

const {
  REACT_APP_SPOTIFY_ARTIST_URL,
  REACT_APP_YOUTUBE_CHANNEL_URL,
  REACT_APP_INSTAGRAM_PAGE_URL,
} = process.env;

const CareerProfile: React.FC<CareerProfileProps> = (props) => {
  const { publicData } = props;
  const { t, i18n } = useTranslation("radar");
  const genres = useSelector((state: any) => state.genres.genres);
  const countries = useSelector((state: any) => state.countries.countries);
  const [artistImage, setArtistImage] = useState<string>("");
  const {
    strmIndexes = {},
    spotify,
    artistMetadata,
    genreStageComp = [],
  } = publicData?.artistRankDetailInfo || {};
  const { followersComp, engagementComp } =
    (genreStageComp && genreStageComp[0]) || {};
  const followersCompoAbove = followersComp >= 0;
  const engagementCompAbove = engagementComp >= 0;
  const { response: topCities } = publicData?.topCities || [];
  const { lastReleases, artistPositionInRankByFilter } = publicData || {};
  const { positionDif, performanceIndexRank } =
    artistPositionInRankByFilter || {};

  const { artistIdSpotify, channelIdYoutube, usernameInstagram } =
    artistMetadata || {};
  const wrappGetI18nGenre = (genre: string) =>
    getI18nGenre(genre, i18n, genres);

  const wrappGetI18nCountry = (country: string) =>
    getI18nCoutry(country, i18n, countries);

  useEffect(() => {
    const image = spotify?.imagesSpotify && spotify?.imagesSpotify[0]?.url;
    if (image) {
      srcToBase64(image).then((dataURL) => {
        setArtistImage(dataURL);
      });
    }
  }, [publicData, spotify]);

  const getAvailableMedia = () => {
    const media: string[] = [
      artistMetadata?.artistIdSpotify ? "Spotify" : "",
      artistMetadata?.channelIdYoutube ? "Youtube" : "",
      artistMetadata?.usernameInstagram ? "Instagram" : "",
    ].filter((media) => media);

    return media.length > 1
      ? `${media.slice(0, -1).join(", ")} ${t("pages.CareerDiagnose.e")} ${
          media[media.length - 1]
        }`
      : media.length === 1
      ? media[0]
      : "";
  };

  // const getfilterPlus = (filterArray: any, limit: number, label: string) => {
  //   if (filterArray && filterArray.length > 0) {
  //     if (filterArray.length > limit) {
  //       const sliced = filterArray.slice(0, limit);
  //       return `${sliced.join(", ")}. (+${
  //         filterArray.length - limit
  //       } ${label})`;
  //     } else {
  //       return `${filterArray.join(", ")}.`;
  //     }
  //   }
  //   return "-";
  // };

  if (!publicData) return null;
  return (
    <>
      <Row gutter={[16, 32]}>
        <Col xs={24}>
          <Row gutter={32}>
            <Col xs={24}>
              <Row>
                <h1 className="texto-colorido title">
                  {t("pages.CareerDiagnose.artistProfile")}
                </h1>
                <div className="platform-icons">
                  <span className="platform-text">
                    {t("pages.CareerDiagnose.platforms")}
                  </span>
                  {artistIdSpotify && (
                    <a
                      href={`${REACT_APP_SPOTIFY_ARTIST_URL}${artistIdSpotify}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <SpotifyIcon />
                    </a>
                  )}
                  {channelIdYoutube && (
                    <a
                      href={`${REACT_APP_YOUTUBE_CHANNEL_URL}${channelIdYoutube}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <YoutubeIcon />
                    </a>
                  )}
                  {usernameInstagram && (
                    <a
                      href={`${REACT_APP_INSTAGRAM_PAGE_URL}${usernameInstagram}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <InstagramIcon />
                    </a>
                  )}
                </div>
              </Row>
            </Col>
            <Col xs={8} style={{ textAlign: "left" }}>
              <Row>
                <Avatar
                  src={artistImage}
                  size={180}
                  style={{ border: "6px solid" }}
                />
              </Row>
              <Row>
                <Col xs={24}>
                  <h1 className="name">{artistMetadata?.artistName}</h1>
                </Col>
                <Col xs={24}>
                  <Row>
                    <Col xs={24}>
                      <span
                        className="country"
                        style={{ marginRight: 12, fontSize: 20 }}
                      >
                        {wrappGetI18nCountry(artistMetadata?.country)}
                      </span>
                      {artistMetadata?.genresStrm?.map((genre: any) => (
                        <Tag
                          key={Math.random()}
                          color="#75C594"
                          className="tag-genre"
                          style={{ fontSize: 16 }}
                        >
                          {wrappGetI18nGenre(genre)}
                        </Tag>
                      ))}
                    </Col>
                  </Row>
                </Col>
                <Col xs={24}>
                  <span style={{ fontSize: 24 }}>
                    {t("pages.ArtistProfile.stage")}
                    {t(
                      `pages.Radar.${
                        careerStages[publicData?.stageCareer - 1 || 0]
                      }`
                    )}
                  </span>
                  <ProgressBar
                    color="#75C594"
                    format={(percent) => `${percent}%`}
                    value={Math.ceil(strmIndexes?.stageProgress * 100 || 0)}
                    trailColor="#00000033"
                  />
                </Col>
              </Row>
            </Col>
            <Col xs={5} offset={1}>
              <div className="card" style={{ height: 270 }}>
                <RankingIcon className="main-icon" />
                {t("pages.ArtistProfile.performanceRankingPosition", {
                  position: performanceIndexRank,
                })}
                ***
                <span
                  className={`${
                    positionDif === null
                      ? "blue"
                      : positionDif > 0
                      ? "green"
                      : positionDif < 0
                      ? "red"
                      : ""
                  } card-text`}
                  style={{ visibility: "hidden" }}
                >
                  {positionDif === null ? (
                    "New"
                  ) : positionDif === 0 ? (
                    <FontAwesomeIcon icon={faStop} />
                  ) : positionDif > 0 ? (
                    <>
                      <FontAwesomeIcon icon={faArrowUp} />
                      {` ${positionDif}`}
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faArrowDown} />
                      {` ${Math.abs(positionDif)}`}
                    </>
                  )}
                </span>
                <span
                  className="font-light weekly-movement"
                  style={{ visibility: "hidden" }}
                >
                  {t("pages.ArtistProfile.weeklyMovement")}
                </span>
              </div>
            </Col>
            <Col xs={5}>
              <div className="card" style={{ height: 270 }}>
                <div
                  style={{ display: "flex", width: "100%", padding: "0 12px" }}
                >
                  <EngagementIcon
                    className="main-icon"
                    style={{ flex: 1, marginLeft: 20 }}
                  />
                  <Popover
                    showArrow={false}
                    content={
                      <div style={{ width: 500 }}>
                        {t("pages.CareerDiagnose.engagementTooltip", {
                          engagementComp: Math.abs(engagementComp),
                          engagementCompAbove: engagementCompAbove
                            ? t("pages.ArtistProfile.aboveAverage")
                            : t("pages.ArtistProfile.belowAverage"),
                        })}
                      </div>
                    }
                    placement="right"
                  >
                    <InfoCircleOutlined
                      style={{
                        fontSize: 20,

                        justifySelf: "flex-end",
                      }}
                    />
                  </Popover>
                </div>

                {t("pages.ArtistProfile.engagement")}
                <span
                  className={`${
                    engagementCompAbove ? "green" : "red"
                  } card-text`}
                >
                  <span style={{ marginRight: "10px" }}>
                    {engagementCompAbove ? (
                      <FontAwesomeIcon icon={faArrowUp} />
                    ) : (
                      <FontAwesomeIcon icon={faArrowDown} />
                    )}
                  </span>
                  {Math.abs(engagementComp)}%
                </span>
                <span className="font-light">
                  {engagementCompAbove
                    ? t("pages.ArtistProfile.aboveAverage")
                    : t("pages.ArtistProfile.belowAverage")}
                </span>
              </div>
            </Col>
            <Col xs={5}>
              <div className="card" style={{ height: 270 }}>
                <div
                  style={{ display: "flex", width: "100%", padding: "0 12px" }}
                >
                  <div style={{ flex: 1, marginLeft: 20, width: 50 }}>
                    <FollowersIcon className="main-icon" />
                  </div>
                  <Popover
                    showArrow={false}
                    content={
                      <div style={{ width: 500 }}>
                        {t("pages.CareerDiagnose.followersTooltip", {
                          followersComp: Math.abs(followersComp),
                          followersCompoAbove: followersCompoAbove
                            ? t("pages.ArtistProfile.aboveAverage")
                            : t("pages.ArtistProfile.belowAverage"),
                        })}
                      </div>
                    }
                    placement="right"
                  >
                    <InfoCircleOutlined
                      style={{
                        fontSize: 20,

                        justifySelf: "flex-end",
                      }}
                    />
                  </Popover>
                </div>
                {t("pages.ArtistProfile.followers")}
                <span
                  className={`card-text ${
                    followersCompoAbove ? "green" : "red"
                  }  `}
                >
                  <span style={{ marginRight: "10px" }}>
                    {followersCompoAbove ? (
                      <FontAwesomeIcon icon={faArrowUp} />
                    ) : (
                      <FontAwesomeIcon icon={faArrowDown} />
                    )}
                  </span>
                  {Math.abs(followersComp)}%
                </span>
                <span className="font-light">
                  {followersCompoAbove
                    ? t("pages.ArtistProfile.aboveAverage")
                    : t("pages.ArtistProfile.belowAverage")}
                </span>
              </div>
            </Col>
            <Col
              xs={7}
              style={{
                textAlign: "start",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
              }}
            >
              <h1
                style={{
                  fontSize: 24,
                  fontWeight: "bolder",
                  textTransform: "uppercase",
                }}
              >
                {t("pages.CareerDiagnose.generalPerformanceChart")}
              </h1>
            </Col>
            {topCities?.length > 0 && (
              <Col offset={10} xs={7} className="col-most-audienced-cities">
                <div
                  className="title-most-audienced-cities"
                  style={{ color: "#32D583" }}
                >
                  {t("pages.ArtistProfile.mostPopularCities")}
                  <SpotifyIcon className="icon-spotify" />
                </div>
                <Row className="most-audienced-cities">
                  {topCities?.slice(0, 5).map((item: any, index: any) => {
                    return (
                      <p className="most-audienced-city" key={`city_${index}`}>
                        <span className="item-list-circle">{index + 1}</span>
                        <b className="most-audienced-city-name">{item.city}</b>
                        {`${item.listenerCount} ${t(
                          "pages.ArtistProfile.listeners"
                        )}`}
                      </p>
                    );
                  })}
                </Row>
              </Col>
            )}
          </Row>
          <h1
            className="page-title-new"
            style={{
              marginBottom: 0,
              margin: "0 auto",
              maxWidth: 400,
              textAlign: "center",
              fontWeight: "bold",
              lineHeight: "100%",
            }}
          >
            {t("pages.Radar.graphicGeneralPerformance")}
          </h1>
          {lastReleases?.artistIndividualMetrics?.series.length === 0 && (
            <div className="no-data-container" style={{ top: 600 }}>
              <div className="no-data-text">
                {t("pages.ArtistProfile.noPerfomanceData")}
              </div>
            </div>
          )}
          <Row justify="center" className="chart">
            <Col xs={20} style={{ width: "100%" }}>
              <Charts.LineChart
                color={["#12B76A"]}
                textColor="#999999"
                categories={
                  lastReleases?.artistIndividualMetrics?.series.map(
                    (serie: any) => new Date(moment(serie).toDate()).getTime()
                  ) || []
                }
                series={[
                  {
                    name: t("pages.ArtistProfile.performanceIndex"),
                    data: lastReleases?.artistIndividualMetrics?.data
                      ?.performanceIndex,
                  },
                ]}
                markerStrokeWidth={0}
                xAxisFormatter={(val) => val?.toLocaleString()}
                height={400}
                xaxisLabelFormat="MM/yy"
                xaxisType="datetime"
                tooltipTitleFormat
                tooltip={{
                  label: t("pages.ArtistProfile.releasesLabel"),
                  points:
                    lastReleases?.artistIndividualMetrics?.markers.map(
                      (serie: any) => ({
                        text: serie.text,
                        x: new Date(moment(serie.x).toDate()).getTime(),
                        y: serie.y.performanceIndex,
                      })
                    ) || [],
                }}
              />
            </Col>
          </Row>
          {lastReleases?.artistIndividualMetrics?.series.length > 0 && (
            <Row
              className="disclaimer-new"
              justify="center"
              style={{ marginTop: "-25px" }}
            >
              <Col style={{ maxWidth: 500, marginTop: 12 }}>
                <p style={{ fontSize: 16, textAlign: "center" }}>
                  {`${t(
                    "pages.CareerDiagnose.sourceDisclaimer"
                  )} ${getAvailableMedia()}`}
                </p>
              </Col>
            </Row>
          )}
          <Row gutter={[16, 32]} justify="center">
            <Col xs={24}>
              <h1 className="page-title-new">
                {t("pages.ArtistProfile.strmIndex")}
              </h1>
              <ProgressBar
                value={(strmIndexes?.performanceIndex || 0).toPrecision(3)}
                format={(value) => `${value} %`}
                color="#36BFFA"
                title={t("pages.ArtistProfile.performanceChart")}
                titleClass="texto-maiusculo"
                strokeWidth={12}
                trailColor="#BCBCBC4D"
              />
              <span className="index-explaination">
                {t("pages.ArtistProfile.performanceExplaination")}
              </span>
              <ProgressBar
                value={(strmIndexes?.developmentIndex || 0).toPrecision(3)}
                format={(value) => `${value} %`}
                color="#F670C7"
                title={t("pages.ArtistProfile.developmentChart")}
                titleClass="texto-maiusculo"
                strokeWidth={12}
                trailColor="#BCBCBC4D"
              />
              <span className="index-explaination">
                {t("pages.ArtistProfile.developmentExplaination")}
              </span>
              <ProgressBar
                value={(strmIndexes?.performanceSpotify || 0).toPrecision(3)}
                format={(value) => `${value} %`}
                color="#F97066"
                title={t("pages.ArtistProfile.audioChart")}
                titleClass="texto-maiusculo"
                strokeWidth={12}
                trailColor="#BCBCBC4D"
              />
              <span className="index-explaination">
                {t("pages.ArtistProfile.performanceSpotifyExplaination")}
              </span>
              <ProgressBar
                value={(strmIndexes?.performanceInstagram || 0).toPrecision(3)}
                format={(value) => `${value} %`}
                color="#32D583"
                title={t("pages.ArtistProfile.imageChart")}
                titleClass="texto-maiusculo"
                strokeWidth={12}
                trailColor="#BCBCBC4D"
              />
              <span className="index-explaination">
                {t("pages.ArtistProfile.performanceInstagramExplaination")}
              </span>
              <ProgressBar
                value={(strmIndexes?.volatilityIndex || 0).toPrecision(3)}
                format={(value) => `${value} %`}
                color="#FDB022"
                title={t("pages.ArtistProfile.volatilityChart")}
                titleClass="texto-maiusculo"
                strokeWidth={12}
                trailColor="#BCBCBC4D"
              />
              <span className="index-explaination">
                {t("pages.ArtistProfile.volatilityExplaination")}
              </span>
            </Col>
          </Row>
          <Row style={{ textAlign: "end" }}>
            <Col xs={24}>
              <Row justify="end">
                <span style={{ fontSize: 22 }}>
                  {t("pages.CareerDiagnose.filterTextExport", {
                    country: wrappGetI18nCountry(artistMetadata?.country),
                  })}
                </span>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default CareerProfile;
